/**
 * useSpeechSynthesis - Premium text-to-speech hook
 * 
 * Features:
 * - Smart voice selection (prioritizes quality)
 * - Character voices (Pip's higher pitch)
 * - Rate control
 * - Queue management
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// Quality scoring for voice selection
const VOICE_SCORES = {
  'google': 100,
  'neural': 90,
  'natural': 85,
  'samantha': 80,
  'karen': 75,
  'moira': 70,
  'fiona': 65,
  'zira': 60,
  'female': 30,
  'en-us': 15,
  'en-gb': 10,
};

export function useSpeechSynthesis() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [rate, setRate] = useState(0.9);

  const synthRef = useRef(null);

  // Get voice quality score
  const getVoiceScore = useCallback((voice) => {
    let score = 0;
    const name = voice.name.toLowerCase();
    const lang = voice.lang.toLowerCase();

    for (const [keyword, points] of Object.entries(VOICE_SCORES)) {
      if (name.includes(keyword) || lang.includes(keyword)) {
        score += points;
      }
    }

    if (voice.localService) score += 5;
    return score;
  }, []);

  // Initialize
  useEffect(() => {
    if (!window.speechSynthesis) {
      setIsSupported(false);
      return;
    }

    setIsSupported(true);
    synthRef.current = window.speechSynthesis;

    const loadVoices = () => {
      const allVoices = synthRef.current.getVoices();
      const englishVoices = allVoices
        .filter(v => v.lang.startsWith('en'))
        .sort((a, b) => getVoiceScore(b) - getVoiceScore(a));

      setVoices(englishVoices);

      if (englishVoices.length > 0 && !selectedVoice) {
        setSelectedVoice(englishVoices[0]);
      }
    };

    loadVoices();
    synthRef.current.onvoiceschanged = loadVoices;

    return () => {
      if (synthRef.current) {
        synthRef.current.cancel();
      }
    };
  }, [getVoiceScore]);

  // Base speak function
  const speak = useCallback((text, options = {}) => {
    return new Promise((resolve) => {
      if (!synthRef.current || !text) {
        resolve();
        return;
      }

      // Cancel any ongoing speech
      synthRef.current.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = options.rate ?? rate;
      utterance.pitch = options.pitch ?? 1.0;
      utterance.volume = options.volume ?? 1.0;

      const voice = options.voice || selectedVoice || voices[0];
      if (voice) {
        utterance.voice = voice;
      }

      utterance.onstart = () => setIsSpeaking(true);
      
      utterance.onend = () => {
        setIsSpeaking(false);
        resolve();
      };

      utterance.onerror = (e) => {
        setIsSpeaking(false);
        // Don't reject - just resolve to not break chains
        if (e.error !== 'interrupted') {
          console.warn('Speech error:', e.error);
        }
        resolve();
      };

      synthRef.current.speak(utterance);
    });
  }, [rate, selectedVoice, voices]);

  // Speak as Pip (child-like voice)
  const speakAsPip = useCallback((text) => {
    return speak(text, {
      rate: rate * 0.95,
      pitch: 1.35, // Higher pitch for character
    });
  }, [speak, rate]);

  // Speak praise (enthusiastic)
  const speakPraise = useCallback((text) => {
    return speak(text, {
      rate: rate * 1.05,
      pitch: 1.15,
    });
  }, [speak, rate]);

  // Cancel speech
  const cancel = useCallback(() => {
    if (synthRef.current) {
      synthRef.current.cancel();
      setIsSpeaking(false);
    }
  }, []);

  // Update rate
  const updateRate = useCallback((newRate) => {
    setRate(Math.max(0.5, Math.min(1.5, newRate)));
  }, []);

  return {
    isSupported,
    isSpeaking,
    voices,
    selectedVoice,
    speak,
    speakAsPip,
    speakPraise,
    cancel,
    setRate: updateRate,
    selectVoice: setSelectedVoice,
  };
}

export default useSpeechSynthesis;
